import type { BarcodeIssueFixItem } from '@/api/handlers/resources/products/requests';
import {
    isBarcodeFixErrorCode,
    type BarcodeFixErrorCode,
} from '@/api/handlers/resources/products/productErrorCodes';
import type { BarcodeFixError } from '@/api/handlers/resources/products/responses';
import type {
    BarcodeFixAction,
    BarcodeIssue,
    GeneratedBarcode,
} from '@/api/models';
import { eanCheckDigitStatus, isDigitsBarcode } from '@/utils/barcodes';

/**
 * The Barcode issues tab's rules (bos-app#239, board `bc-Repair`): which
 * fixes a listed barcode offers, what one fix call sends, and how a refusal
 * maps back to the row it blames. Pure, so the hook and the tests share it.
 */

/**
 * One listed barcode's key. A variant can list its main and several
 * alternates, so the variant alone is not enough.
 */
export const barcodeIssueKeyOf = (issue: BarcodeIssue): string =>
    `${issue.variantId}|${issue.role}|${issue.barcode}`;

/**
 * The fixes a row offers, the suggestion first (contract rule 6): a main
 * that is not digits generates, types or keeps; a scrambled main generates
 * or types, as its digits are lost; an alternate is removed, typed or kept.
 */
export const fixOptionsOf = (
    issue: BarcodeIssue,
): readonly BarcodeFixAction[] => {
    if (issue.role === 'alternate') {
        return ['remove', 'type', 'keep'];
    }
    return issue.problem === 'SCIENTIFIC_NOTATION'
        ? ['generate', 'type']
        : ['generate', 'type', 'keep'];
};

/** The row's suggested fix, what its select starts on. */
export const suggestedFixOf = (issue: BarcodeIssue): BarcodeFixAction =>
    issue.role === 'alternate' ? 'remove' : 'generate';

/**
 * Which problem copy a row reads: a main that holds its own SKU says so, an
 * alternate that is not digits says it is an extra barcode.
 */
export const problemCopyKeyOf = (issue: BarcodeIssue): string => {
    if (issue.problem === 'SCIENTIFIC_NOTATION') {
        return 'SCIENTIFIC_NOTATION';
    }
    if (issue.role === 'alternate') {
        return 'NOT_DIGITS_alternate';
    }
    return issue.sku !== null && issue.barcode === issue.sku
        ? 'NOT_DIGITS_sku'
        : 'NOT_DIGITS';
};

/**
 * The typed code as the row judges it (contract rule 7), the same reading as
 * the import drawer's: `notDigits` is refused; `checkDigit` is an 8- or
 * 13-digit code whose check digit doesn't match, until **Use it anyway**
 * for that code (`usedAnyway`); any other length is not checked.
 */
export type TypedCodeStatus =
    'empty' | 'notDigits' | 'checkDigit' | 'usedAnyway' | 'ok';

export const typedCodeStatusOf = (
    typed: string,
    usedAnyway: string | null,
): TypedCodeStatus => {
    const code = typed.trim();
    if (code === '') {
        return 'empty';
    }
    if (!isDigitsBarcode(code)) {
        return 'notDigits';
    }
    if (eanCheckDigitStatus(code) !== 'invalid') {
        return 'ok';
    }
    return usedAnyway === code ? 'usedAnyway' : 'checkDigit';
};

/** What the owner chose on one row. */
export type BarcodeIssueChoice = {
    fix: BarcodeFixAction;
    typed: string;
    /** The typed code the owner confirmed with Use it anyway. */
    usedAnyway: string | null;
};

/** The row's choice, its suggestion until the owner picks another. */
export const choiceOf = (
    choices: Readonly<Record<string, BarcodeIssueChoice>>,
    issue: BarcodeIssue,
): BarcodeIssueChoice =>
    choices[barcodeIssueKeyOf(issue)] ?? {
        fix: suggestedFixOf(issue),
        typed: '',
        usedAnyway: null,
    };

/** Whether a row can be sent as chosen: a typed code must be accepted. */
export const isChoiceReady = (choice: BarcodeIssueChoice): boolean => {
    if (choice.fix !== 'type') {
        return true;
    }
    const status = typedCodeStatusOf(choice.typed, choice.usedAnyway);
    return status === 'ok' || status === 'usedAnyway';
};

/**
 * What the bar sends: every selected row with its suggestion, every one kept
 * (keep takes the whole variant), or every one as chosen in its select.
 */
export type BarcodeIssuesSubmitMode = 'suggestions' | 'keep' | 'chosen';

const fixItemOf = (
    issue: BarcodeIssue,
    fix: BarcodeFixAction,
    typed: string,
): BarcodeIssueFixItem => ({
    variantId: issue.variantId,
    role: issue.role,
    barcode: issue.barcode,
    action: fix,
    ...(fix === 'type' ? { newBarcode: typed.trim() } : {}),
});

/** The fix call's items, one per selected row, in the order given. */
export const buildFixItems = (
    issues: readonly BarcodeIssue[],
    mode: BarcodeIssuesSubmitMode,
    choices: Readonly<Record<string, BarcodeIssueChoice>>,
): BarcodeIssueFixItem[] =>
    issues.map((issue) => {
        if (mode === 'suggestions') {
            return fixItemOf(issue, suggestedFixOf(issue), '');
        }
        if (mode === 'keep') {
            return fixItemOf(issue, 'keep', '');
        }
        const choice = choiceOf(choices, issue);
        return fixItemOf(issue, choice.fix, choice.typed);
    });

/** How many variants the rows belong to, which "Keep N as is" counts. */
export const variantCountOf = (issues: readonly BarcodeIssue[]): number =>
    new Set(issues.map((issue) => issue.variantId)).size;

/** The refusals shown on the row `itemIndex` names. */
const ROW_CODES: readonly BarcodeFixErrorCode[] = [
    'BARCODE_NOT_DIGITS',
    'BARCODE_TAKEN',
    'BARCODE_DUPLICATE',
    'BARCODE_ACTION_INVALID',
];

/**
 * A refused fix, as the tab shows it; nothing was applied.
 * - `row`: a row code with the row it blames, shown there and in the alert;
 * - `general`: a row code with no row, or `BARCODE_FIX_INVALID`;
 * - `changed`: 409 `BARCODE_CHANGED`, asking to reload, naming the row when
 *   one is blamed;
 * - `branch`: the list's branch scope refused (`BRANCH_NOT_ALLOWED`,
 *   `BRANCH_NOT_FOUND`);
 * - `refused`: any other 4xx: the server answered, so nothing was applied;
 * - `unknown`: no answer, or a server failure (5xx): the fix may have gone
 *   through, so the tab asks to reload before trying again (review of #241,
 *   finding 9).
 */
export type BarcodeIssuesRefusal =
    | {
          kind: 'row';
          code: BarcodeFixErrorCode;
          rowKey: string;
          productName: string;
      }
    | { kind: 'general'; code: BarcodeFixErrorCode }
    | { kind: 'changed'; productName: string | null }
    | { kind: 'branch' }
    | { kind: 'refused' }
    | { kind: 'unknown' };

/** The server answered with a 4xx, which the API applies all or nothing. */
const isAnswered = (error: BarcodeFixError): boolean =>
    typeof error.status === 'number' &&
    error.status >= 400 &&
    error.status < 500;

/**
 * Reads a refusal against the rows that were sent, `itemIndex` being the
 * 0-based index into them.
 */
export const readRefusal = (
    error: BarcodeFixError,
    sent: readonly BarcodeIssue[],
): BarcodeIssuesRefusal => {
    const code = error.code;
    if (code === 'BRANCH_NOT_ALLOWED' || code === 'BRANCH_NOT_FOUND') {
        return { kind: 'branch' };
    }
    if (!isBarcodeFixErrorCode(code)) {
        return isAnswered(error) ? { kind: 'refused' } : { kind: 'unknown' };
    }
    const blamed =
        error.itemIndex === undefined ? undefined : sent[error.itemIndex];
    if (code === 'BARCODE_CHANGED') {
        return { kind: 'changed', productName: blamed?.productName ?? null };
    }
    if (ROW_CODES.includes(code) && blamed !== undefined) {
        return {
            kind: 'row',
            code,
            rowKey: barcodeIssueKeyOf(blamed),
            productName: blamed.productName,
        };
    }
    return { kind: 'general', code };
};

/** A fix that went through, for the success alert and the label list. */
export type BarcodeIssuesSuccess = {
    fixed: number;
    generatedBarcodes: GeneratedBarcode[];
    /** When it was applied, which names the label list's file. */
    fixedAt: string;
};

/** The labels to print for the generated barcodes: their units summed. */
export const labelUnitsOf = (rows: readonly GeneratedBarcode[]): number =>
    rows.reduce((sum, row) => sum + Math.max(0, row.units), 0);
